import React, { useState, useEffect } from "react"
import { Enrollment } from "../../../../service/enrollment"
import { Link } from "react-router-dom"
import DiscountModal from "../discount-modal"
import { useSelector } from "react-redux"
import { selectUser } from "../../../../core/data/redux/slice/user"
import CameraModal from "../../../admission/registration/cameraModal"
import {
  updateStudentProfilePicture,
  removeStudentProfilePicture,
  uploadProfilePictureFromCamera,
} from "../../../../service/enrollment"
import {
  validateImageFile,
  getProfilePictureUrl,
  hasProfilePicture,
} from "../../../../service/enrollment/helpers"
import { toast } from "react-toastify"

interface StudentSidebarProps {
  data: Enrollment
  fetchData: () => void
}

const StudentSidebar: React.FC<StudentSidebarProps> = ({ data, fetchData }) => {
  const [selectedStudent, setSelectedStudent] = useState<Enrollment | null>(
    null,
  )
  const user = useSelector(selectUser)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showCamera, setShowCamera] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [currentImage, setCurrentImage] = useState<string>(
    "/assets/img/students/student-01.jpg",
  )

  useEffect(() => {
    if (data) {
      const imageUrl = getProfilePictureUrl(data)
      setCurrentImage(imageUrl)
    }
  }, [data])

  const handleOpenDiscountModal = (studentData: Enrollment) => {
    setSelectedId(studentData._id)
    setSelectedStudent(studentData)
  }

  const isDiscountExist = data?.discounts && data.discounts.length > 0

  const handleCapture = async (imageSrc: string) => {
    try {
      setShowCamera(false)

      if (!data?._id) {
        toast.error("Student ID not found")
        return
      }

      setIsUploading(true)

      const response = await uploadProfilePictureFromCamera(data._id, imageSrc)

      if (response.success) {
        if (response.data.profilePicture) {
          setCurrentImage(response.data.profilePicture)
        }

        if (fetchData) {
          setTimeout(() => {
            fetchData()
          }, 1000)
        }

        toast.success("Profile picture updated successfully!")
      } else {
        toast.error(response.message || "Failed to update profile picture")
      }
    } catch (error: any) {
      console.error("Error capturing image:", error)
      toast.error(error.message || "Failed to update profile picture")
    } finally {
      setIsUploading(false)
    }
  }

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0]
    if (!file) return

    const validation = validateImageFile(file)
    if (!validation.isValid) {
      toast.error(validation.error)
      return
    }

    if (!data?._id) {
      toast.error("Student ID not found")
      return
    }

    try {
      setIsUploading(true)

      const response = await updateStudentProfilePicture(data._id, file)

      if (response.success) {
        if (response.data.profilePicture) {
          setCurrentImage(response.data.profilePicture)
        }

        if (fetchData) {
          setTimeout(() => {
            fetchData()
          }, 1000)
        }

        toast.success("Profile picture updated successfully!")
      } else {
        toast.error(response.message || "Failed to update profile picture")
      }
    } catch (error: any) {
      console.error("Error uploading profile picture:", error)
      toast.error(error.message || "Failed to upload profile picture")
    } finally {
      setIsUploading(false)
    }
  }

  const getImageUrl = () => {
    if (isUploading) {
      return "/assets/img/students/student-01.jpg"
    }
    return currentImage
  }

  const handleRemovePhoto = async () => {
    if (!data?._id) {
      toast.error("Student ID not found")
      return
    }

    if (!hasProfilePicture(data)) {
      toast.error("No profile picture found to remove")
      return
    }

    try {
      setIsUploading(true)

      const response = await removeStudentProfilePicture(data._id)

      if (response.success) {
        setCurrentImage("/assets/img/students/student-01.jpg")

        if (fetchData) {
          setTimeout(() => {
            fetchData()
          }, 500)
        }

        toast.success("Profile picture removed successfully!")
      } else {
        toast.error(response.message || "Failed to remove profile picture")
      }
    } catch (error: any) {
      console.error("Error removing profile picture:", error)
      toast.error(error.message || "Failed to remove profile picture")
    } finally {
      setIsUploading(false)
    }
  }

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A"
    try {
      const date = new Date(dateString)
      if (isNaN(date.getTime())) return "N/A"
      return date.toLocaleDateString("en-GB")
    } catch (error) {
      return "N/A"
    }
  }

  const getStudentName = () => {
    if (data?.user) {
      return `${data.user.firstName || ""} ${data.user.lastName || ""}`.trim()
    }
    return "N/A"
  }

  return (
    <div className="col-xxl-3 col-xl-4 theiaStickySidebar">
      <div className="stickybar pb-4">
        <div className="card border-white">
          <div className="card-header">
            <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              {/* Left Side - Student Info with Image */}
              <div className="d-flex align-items-center flex-grow-1">
                <div className="d-flex align-items-center justify-content-center avatar avatar-xxl border border-dashed me-3 flex-shrink-0 text-dark frames">
                  {isUploading ? (
                    <div className="d-flex align-items-center justify-content-center w-100 h-100">
                      <div
                        className="spinner-border spinner-border-sm text-primary"
                        role="status"
                      >
                        <span className="visually-hidden">Uploading...</span>
                      </div>
                    </div>
                  ) : currentImage !== "/assets/img/students/student-01.jpg" ? (
                    <img
                      src={getImageUrl()}
                      alt="Student"
                      className="img-fluid rounded-circle"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      onError={e => {
                        console.error("Image failed to load:", currentImage)
                        e.currentTarget.src =
                          "/assets/img/students/student-01.jpg"
                      }}
                    />
                  ) : (
                    <i className="ti ti-photo-plus fs-16" />
                  )}
                </div>

                <div className="overflow-hidden">
                  <span className="badge badge-soft-success d-inline-flex align-items-center mb-1">
                    <i className="ti ti-circle-filled fs-5 me-1" />
                    {data?.user?.status || "N/A"}
                  </span>
                  <h5 className="mb-1 text-truncate">{getStudentName()}</h5>
                  <span className="badge badge-soft-primary fs-14 fw-bold px-2 py-1">
                    GR # {data?.student?.grNumber || "N/A"}
                  </span>
                </div>
              </div>

              {/* Right Side - Upload Options */}
              <div
                className="profile-upload ms-3"
                style={{ minWidth: "250px" }}
              >
                <div
                  className="profile-uploader d-flex align-items-center justify-content-center mb-3 gap-2"
                  style={{ flexWrap: "nowrap" }}
                >
                  <div
                    className="drag-upload-btn position-relative flex-grow-1 text-center"
                    style={{ flexBasis: "0" }}
                  >
                    <i className="ti ti-upload me-1 fs-12"></i>
                    <span className="small">Upload</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="form-control image-sign position-absolute top-0 start-0 w-100 h-100 opacity-0"
                      name="profilePicture"
                      onChange={handleFileChange}
                      disabled={isUploading}
                    />
                  </div>

                  <div
                    className="drag-upload-btn position-relative flex-grow-1 text-center"
                    style={{ flexBasis: "0" }}
                  >
                    <i className="ti ti-camera me-1 fs-12"></i>
                    <span className="small text-nowrap">Take Photo</span>
                    <button
                      type="button"
                      className="btn p-0 border-0 bg-transparent w-100 h-100 position-absolute top-0 start-0"
                      onClick={() => setShowCamera(true)}
                      disabled={isUploading}
                    />
                  </div>

                  {hasProfilePicture(data) &&
                    currentImage !== "/assets/img/students/student-01.jpg" && (
                      <button
                        className="btn btn-danger btn-sm flex-grow-1 text-center"
                        type="button"
                        onClick={handleRemovePhoto}
                        disabled={isUploading}
                        style={{
                          flexBasis: "0",
                          padding: "0.3rem 0.5rem",
                          fontSize: "0.75rem",
                        }}
                        title="Remove profile picture"
                      >
                        {isUploading ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                          >
                            <span className="visually-hidden">Removing...</span>
                          </span>
                        ) : (
                          <i className="ti ti-trash"></i>
                        )}
                      </button>
                    )}
                </div>
                <p className="fs-12 mb-0">
                  Upload image size 4MB, Format JPG, PNG, SVG
                </p>
                {isUploading && (
                  <div className="text-info small mt-1">
                    <i className="ti ti-refresh spinner me-1"></i>
                    {currentImage !== "/assets/img/students/student-01.jpg"
                      ? "Updating"
                      : "Uploading"}{" "}
                    picture...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div className="card-body">
            <h5 className="mb-3">Basic Information</h5>
            <dl className="row mb-0">
              <dt className="col-6 fw-medium text-dark mb-3">GR No</dt>
              <dd className="col-6 mb-3">{data?.student?.grNumber || "N/A"}</dd>

              <dt className="col-6 fw-medium text-dark mb-3">Gender</dt>
              <dd className="col-6 mb-3">{data?.user?.gender || "N/A"}</dd>

              <dt className="col-6 fw-medium text-dark mb-3">Date Of Birth</dt>
              <dd className="col-6 mb-3">
                {formatDate(data?.student?.dateOfBirth)}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">
                Date Of Admission
              </dt>
              <dd className="col-6 mb-3">
                {/* Real historical admission date when known; falls back to
                    this record's creation date for legacy students without it. */}
                {formatDate(
                  data?.admission?.admissionDate || data?.student?.createdAt,
                )}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">Blood Group</dt>
              <dd className="col-6 mb-3">{data?.user?.bloodGroup || "N/A"}</dd>

              <dt className="col-6 fw-medium text-dark mb-3">Religion</dt>
              <dd className="col-6 mb-3">
                {data?.user?.religion || "N/A"}
                {data?.admission?.religionType
                  ? ` (${
                      data.admission.religionType === "other"
                        ? data.admission.religionTypeOther || "other"
                        : data.admission.religionType
                    })`
                  : ""}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">Mother tongue</dt>
              <dd className="col-6 mb-3">
                {data?.student?.motherTongue || "N/A"}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">Nationality</dt>
              <dd className="col-6 mb-3">
                {data?.admission?.nationality || "N/A"}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">
                Place Of Birth
              </dt>
              <dd className="col-6 mb-3">
                {data?.admission?.placeOfBirth || "N/A"}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">B-Form No</dt>
              <dd className="col-6 mb-3">
                {data?.admission?.bFormNo || "N/A"}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">
                Siblings At School
              </dt>
              <dd className="col-6 mb-3">
                {data?.admission?.noOfSiblingsEnrolled ?? "N/A"}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">Area</dt>
              <dd className="col-6 mb-3">
                {data?.admission?.area || "N/A"}
              </dd>

              <dt className="col-6 fw-medium text-dark mb-3">City</dt>
              <dd className="col-6 mb-3">
                {data?.admission?.city || "N/A"}
              </dd>

              {data?.admission?.sameAsCurrentAddress === false && (
                <>
                  <dt className="col-6 fw-medium text-dark mb-3">
                    Permanent Address
                  </dt>
                  <dd className="col-6 mb-3">
                    {[
                      data.admission.permanentHouseNo,
                      data.admission.permanentBlockNo,
                      data.admission.permanentBuildingName,
                      data.admission.permanentArea,
                      data.admission.permanentCity,
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </dd>
                </>
              )}

              {(!user.permissions?.length ||
                user.permissions.includes("profile-discount.view")) && (
                <>
                  <dt className="col-6 fw-medium text-dark mb-3">Discount</dt>
                  <dd className="col-6 mb-3">
                    {data?.discounts && data.discounts.length > 0
                      ? data.discounts
                          .map(
                            discount =>
                              `${discount.discount}${
                                discount.discountType === "percentage"
                                  ? "%"
                                  : " PKR"
                              }`,
                          )
                          .join(", ")
                      : "N/A"}
                  </dd>
                </>
              )}
            </dl>

            {(!user.permissions?.length ||
              user.permissions?.includes(
                isDiscountExist
                  ? "profile-discount.edit"
                  : "profile-discount.create",
              )) && (
              <Link
                to="#"
                data-bs-toggle="modal"
                data-bs-target="#discount-modal"
                className="btn btn-primary btn-sm w-100"
                onClick={() => handleOpenDiscountModal(data)}
              >
                {isDiscountExist ? "Update Discount" : "Add Discount"}
              </Link>
            )}
          </div>
        </div>

        {/* Primary Contact Info */}
        <div className="card border-white">
          <div className="card-body">
            <h5 className="mb-3">Primary Contact Info</h5>
            <div className="d-flex align-items-center mb-3">
              <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                <i className="ti ti-phone" />
              </span>
              <div>
                <span className="text-dark fw-medium mb-1 text-nowrap">
                  Phone Number
                </span>
                <p>{data?.user?.phoneNumber || "N/A"}</p>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <span className="avatar avatar-md bg-light-300 rounded me-2 flex-shrink-0 text-default">
                <i className="ti ti-mail" />
              </span>
              <div>
                <span className="text-dark fw-medium mb-1 text-nowrap">
                  Email Address
                </span>
                <p>{data?.user?.email || "N/A"}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Discount Modal */}
      <DiscountModal
        studentData={selectedStudent}
        fetchData={fetchData}
        selectedId={selectedId}
        setSelectedId={setSelectedId}
      />

      {/* Camera Modal */}
      {showCamera && (
        <CameraModal
          onCapture={handleCapture}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  )
}

export default StudentSidebar
